// 확신도 맞히기에 쓰는 사실 목록 — 값이 분명하고 출처마다 거의 같은 것만 넣는다.
// 출처마다 값이 조금씩 다른 분류(인구·면적)는 비교 문제를 8% 이상 차이 나는 것끼리만 낸다 (calib.ts MIN_RATIO).
import { hasBatchim } from './rng';

export type FactCat = 'year' | 'peak' | 'tower' | 'pop' | 'area' | 'count' | 'misc';

export interface Fact {
  id: string;
  cat: FactCat;
  name: string;
  v: number;
  unit: string;
  /** 구간 문제 질문 (없으면 분류 기본 질문) */
  q?: string;
  note?: string;
}

// 연도 (서기)
const YEARS: [string, number][] = [
  ['백제 멸망', 660], ['고구려 멸망', 668], ['고려 건국', 918], ['칭기즈 칸 즉위', 1206], ['마그나 카르타 서명', 1215],
  ['팔만대장경 완성', 1251], ['조선 건국', 1392], ['세종 즉위', 1418], ['훈민정음 반포', 1446], ['콘스탄티노폴리스 함락', 1453],
  ['콜럼버스 아메리카 도착', 1492], ['루터 종교 개혁 시작', 1517], ['임진왜란 발발', 1592], ['명량 해전', 1597], ['셰익스피어 사망', 1616],
  ['병자호란', 1636], ['뉴턴 「프린키피아」 출간', 1687], ['제임스 와트 증기 기관 특허', 1769], ['미국 독립 선언', 1776], ['프랑스 혁명', 1789],
  ['워털루 전투', 1815], ['다윈 「종의 기원」 출간', 1859], ['미국 남북 전쟁 시작', 1861], ['링컨 노예 해방 선언', 1863], ['일본 메이지 유신', 1868],
  ['강화도 조약', 1876], ['벨 전화기 특허', 1876], ['갑신정변', 1884], ['동학 농민 운동', 1894], ['청일 전쟁 발발', 1894],
  ['대한제국 선포', 1897], ['첫 노벨상 시상', 1901], ['라이트 형제 첫 동력 비행', 1903], ['러일 전쟁 발발', 1904], ['을사늑약', 1905],
  ['아인슈타인 특수 상대성 이론', 1905], ['국권 피탈(경술국치)', 1910], ['타이타닉호 침몰', 1912], ['1차 세계 대전 발발', 1914], ['러시아 혁명', 1917],
  ['3·1 운동', 1919], ['페니실린 발견', 1928], ['미국 대공황 시작', 1929], ['2차 세계 대전 발발', 1939], ['광복', 1945],
  ['인도 독립', 1947], ['대한민국 정부 수립', 1948], ['중화인민공화국 수립', 1949], ['6·25 전쟁 발발', 1950], ['6·25 정전 협정', 1953],
  ['에베레스트 첫 등정', 1953], ['스푸트니크 1호 발사', 1957], ['4·19 혁명', 1960], ['현대자동차 설립', 1967], ['삼성전자 설립', 1969],
  ['인류 첫 달 착륙', 1969], ['경부고속도로 개통', 1970], ['서울 지하철 1호선 개통', 1974], ['마이크로소프트 설립', 1975], ['애플 설립', 1976],
  ['체르노빌 원전 사고', 1986], ['서울 올림픽', 1988], ['베를린 장벽 붕괴', 1989], ['소련 해체', 1991], ['금융 실명제 실시', 1993],
  ['아마존 설립', 1994], ['윈도우 95 출시', 1995], ['IMF 외환 위기', 1997], ['넷플릭스 설립', 1997], ['구글 설립', 1998],
  ['네이버 설립', 1999], ['9·11 테러', 2001], ['한일 월드컵', 2002], ['테슬라 설립', 2003], ['KTX 개통', 2004],
  ['페이스북 설립', 2004], ['유튜브 설립', 2005], ['첫 아이폰 발표', 2007], ['비트코인 백서 발표', 2008], ['카카오톡 출시', 2010],
  ['후쿠시마 원전 사고', 2011], ['평창 동계 올림픽', 2018], ['WHO 코로나19 팬데믹 선언', 2020], ['챗GPT 공개', 2022],
];

// 산 높이 (m)
const PEAKS: [string, number][] = [
  ['에베레스트', 8849], ['K2', 8611], ['칸첸중가', 8586], ['아콩카과', 6961], ['데날리', 6190], ['킬리만자로', 5895], ['엘브루스', 5642],
  ['몽블랑', 4806], ['마터호른', 4478], ['옥산(대만)', 3952], ['후지산', 3776], ['백두산', 2744], ['한라산', 1947], ['지리산', 1915],
  ['설악산', 1708], ['덕유산', 1614], ['태백산', 1567], ['소백산', 1439], ['북한산', 836], ['도봉산', 740], ['관악산', 632],
];

// 건물·탑 높이 (m) — 안테나 포함 여부로 값이 갈리는 곳은 뺐다
const TOWERS: [string, number][] = [
  ['부르즈 할리파', 828], ['메르데카 118', 679], ['도쿄 스카이트리', 634], ['상하이 타워', 632], ['롯데월드타워', 555],
  ['원 월드 트레이드 센터', 541], ['타이베이 101', 508], ['페트로나스 트윈 타워', 452], ['부산 엘시티 랜드마크타워', 412],
  ['63빌딩', 249], ['남산서울타워(탑 자체)', 237],
];

// 인구 (만 명, 2024년 무렵)
const POPS: [string, number][] = [
  ['인도', 145000], ['중국', 141000], ['미국', 34500], ['인도네시아', 28300], ['파키스탄', 25100], ['나이지리아', 23300],
  ['브라질', 21200], ['방글라데시', 17400], ['러시아', 14400], ['멕시코', 13100], ['일본', 12400], ['필리핀', 11600],
  ['베트남', 10100], ['독일', 8450], ['영국', 6900], ['이탈리아', 5900], ['대한민국', 5170], ['스페인', 4850],
  ['캐나다', 4100], ['호주', 2700], ['대만', 2340], ['싱가포르', 600], ['몽골', 350],
];

// 면적 (만 km²)
const AREAS: [string, number][] = [
  ['러시아', 1710], ['캐나다', 998], ['미국', 983], ['중국', 960], ['브라질', 852], ['호주', 769], ['인도', 329],
  ['아르헨티나', 278], ['카자흐스탄', 272], ['알제리', 238], ['인도네시아', 191], ['몽골', 156], ['이집트', 100],
  ['튀르키예', 78], ['스페인', 50.6], ['스웨덴', 45], ['일본', 37.8], ['독일', 35.8], ['베트남', 33.1], ['이탈리아', 30.1],
  ['영국', 24.3], ['북한', 12.1], ['대한민국', 10], ['대만', 3.6],
];

// 개수 — [이름, 값, 단위]
const COUNTS: [string, number, string][] = [
  ['성인의 뼈 개수', 206, '개'], ['피아노 건반 수', 88, '개'], ['바둑판 교차점 수', 361, '개'], ['체스판 칸 수', 64, '칸'],
  ['트럼프 카드 수(조커 제외)', 52, '장'], ['주기율표 원소 수', 118, '개'], ['사람 염색체 수', 46, '개'],
  ['성인 치아 수(사랑니 포함)', 32, '개'], ['미국의 주 개수', 50, '개'], ['UN 회원국 수', 193, '개국'], ['영어 알파벳 수', 26, '자'],
  ['한글 기본 자모 수', 24, '자'], ['대한민국 시·도(광역 자치 단체) 수', 17, '개'], ['2026 월드컵 참가국 수', 48, '개국'],
  ['하루를 분으로 바꾼 수', 1440, '분'], ['1년(365일)을 시간으로 바꾼 수', 8760, '시간'], ['일주일을 분으로 바꾼 수', 10080, '분'],
  ['1시간을 초로 바꾼 수', 3600, '초'], ['원 한 바퀴 각도', 360, '도'], ['태양계 행성 수', 8, '개'], ['축구 한 팀 경기 인원', 11, '명'],
  ['루빅스 큐브 겉면 작은 칸 수', 54, '칸'], ['서울 자치구 수', 25, '개'], ['국회의원 정원', 300, '명'],
];

// 구간 문제 전용 — [이름, 값, 단위, 질문, 참고]
const MISC: [string, number, string, string, string?][] = [
  ['빛의 속도', 299792, 'km', '빛은 1초에 몇 km 갈까?'],
  ['소리의 속도', 343, 'm', '소리는 공기 중(20°C)에서 1초에 몇 m 갈까?'],
  ['지구 둘레', 40075, 'km', '지구 둘레(적도)는 몇 km?'],
  ['지구 반지름', 6371, 'km', '지구 반지름은 몇 km?'],
  ['달 지름', 3474, 'km', '달의 지름은 몇 km?'],
  ['달까지 거리', 38.44, '만 km', '지구에서 달까지 평균 거리는 몇 만 km?'],
  ['태양까지 거리', 14960, '만 km', '지구에서 태양까지 평균 거리는 몇 만 km?'],
  ['햇빛이 지구까지 오는 시간', 499, '초', '햇빛이 태양에서 지구까지 오는 데 몇 초?', '약 8분 19초'],
  ['지구의 나이', 45.4, '억 년', '지구의 나이는 몇 억 년?', '흔히 46억 년이라고 함'],
  ['우주의 나이', 138, '억 년', '우주의 나이는 몇 억 년?'],
  ['금이 녹는 온도', 1064, '°C', '금은 몇 °C 에서 녹을까?'],
  ['철이 녹는 온도', 1538, '°C', '철은 몇 °C 에서 녹을까?'],
  ['마라톤 거리', 42.195, 'km', '마라톤 풀코스는 몇 km?'],
  ['남자 100m 세계 기록', 9.58, '초', '남자 100m 세계 기록(우사인 볼트)은 몇 초?'],
  ['1마일', 1.609, 'km', '1마일은 몇 km?'],
  ['1인치', 2.54, 'cm', '1인치는 몇 cm?'],
  ['1평', 3.306, 'm²', '1평은 몇 m²?'],
  ['1파운드', 453.6, 'g', '1파운드(무게)는 몇 g?'],
  ['1온스', 28.35, 'g', '1온스(무게)는 몇 g?'],
  ['1갤런', 3.785, 'L', '1갤런(미국)은 몇 L?'],
  ['1해리', 1852, 'm', '1해리(바다 거리)는 몇 m?'],
  ['2025년 최저 시급', 10030, '원', '2025년 최저 시급은 몇 원?'],
  ['2026년 최저 시급', 10320, '원', '2026년 최저 시급은 몇 원?'],
  ['부가가치세율', 10, '%', '부가가치세 기본 세율은 몇 %?'],
  ['주휴수당 기준 시간', 15, '시간', '주휴수당을 주는 기준: 1주에 몇 시간 이상 일하면?'],
  ['주 최대 근로 시간', 52, '시간', '법정 근로 시간에 연장 근로까지 합치면 1주 최대 몇 시간?', '법정 40시간 + 연장 12시간 (주 52시간제)'],
];

const fact = (cat: FactCat, name: string, v: number, unit: string, q?: string, note?: string): Fact => ({ id: `${cat}:${name}`, cat, name, v, unit, q, note });

export const FACTS: Fact[] = [
  ...YEARS.map(([n, v]) => fact('year', n, v, '년')),
  ...PEAKS.map(([n, v]) => fact('peak', n, v, 'm')),
  ...TOWERS.map(([n, v]) => fact('tower', n, v, 'm')),
  ...POPS.map(([n, v]) => fact('pop', n, v, '만 명')),
  ...AREAS.map(([n, v]) => fact('area', n, v, '만 km²')),
  ...COUNTS.map(([n, v, u]) => fact('count', n, v, u)),
  ...MISC.map(([n, v, u, q, note]) => fact('misc', n, v, u, q, note)),
];

const eunNeun = (w: string) => `${w}${hasBatchim(w) ? '은' : '는'}`;

export function intervalQuestion(f: Fact): string {
  if (f.q) return f.q;
  switch (f.cat) {
    case 'year': return `${f.name} — 몇 년?`;
    case 'peak': case 'tower': return `${f.name}의 높이는 몇 m?`;
    case 'pop': return `${f.name}의 인구는 2024년 무렵 몇 만 명?`;
    case 'area': return `${f.name}의 면적은 몇 만 km²?`;
    default: return `${eunNeun(f.name)}?`;
  }
}

const num = (v: number) => v.toLocaleString('ko-KR', { maximumFractionDigits: 3 });

/** 값을 읽기 좋게 — 14억 5,000만 명, 1,947 m, 1592년 */
export function formatValue(f: Fact, v = f.v): string {
  if (f.cat === 'year') return `${v}년`;
  if (f.unit === '만 명' && v >= 10000) {
    const eok = Math.floor(v / 10000), man = v % 10000;
    return `${eok}억${man ? ` ${num(man)}만` : ''} 명`;
  }
  return /^[a-zA-Z]/.test(f.unit) ? `${num(v)} ${f.unit}` : `${num(v)}${f.unit}`;
}
